<template>
  <div class="card">
    <div class="body">
      <validation-observer
        ref="form"
        v-show="!finished"
      >
        <form @submit.prevent="submitForm">
          <form-wizard
            class="wizard"
            step-size="sm"
            title=""
            subtitle=""
            next-button-text="Avançar"
            back-button-text="Voltar"
            finish-button-text="Finalizar"
            :start-index.sync="step"
            @on-change="changeTab"
            ref="wizard"
          >
            <tab-content
              class="tab tab-step1"
              title="Consultas"
              :before-change="validateForm"
            >
              <template v-if="step === 0">
                <error-cant-fetch v-if="errors.cantFetch"></error-cant-fetch>
                <step-consultations v-model="form" :tag="tag" v-else/>
              </template>
            </tab-content>
            <tab-content
              class="tab tab-step2"
              title="Informações"
              :before-change="validateForm"
            >
              <step-information :tag="tag" v-model="form" v-if="step === 1"/>
            </tab-content>
            <tab-content
              class="tab tab-step3"
              title="Finalizar"
              :before-change="validateForm"
            >
              <template v-if="step === 2">
                <error-cant-create
                  v-if="errors.cantCreate"
                  :unprocessable-entity="errors.unprocessableEntityWhenCreate"
                  :payment-required="errors.paymentRequiredWhenCreate"
                />
                <step-finish :tag="tag" v-model="form"/>
              </template>
            </tab-content>

            <template slot="footer" slot-scope="props">
              <div class="wizard-footer-header" v-if="!errors.cantFetch">
                Valor da consulta: <strong>R$ {{ price | currency }}</strong>
              </div>
              <div class="wizard-footer-buttons" v-if="!errors.cantFetch">
                <wizard-button
                  @click.native="props.prevTab()"
                  :style="props.fillButtonStyle"
                  :disabled="props.activeTabIndex === 0"
                  class="prev"
                >Voltar</wizard-button>
                <wizard-button
                  @click.native="backToOthers"
                  :style="props.fillButtonStyle"
                  :disabled="step > 0 || tag.includes('cpf', 'cnpj', 'veicles')"
                  class="prev"
                >Voltar</wizard-button>
                <wizard-button
                  v-if="!props.isLastStep"
                  @click.native="props.nextTab()"
                  :style="props.fillButtonStyle"
                  class="next"
                >Avançar</wizard-button>
                <wizard-button
                  v-else
                  @click.native="createQuery()"
                  :style="props.fillButtonStyle"
                  class="finish"
                >Finalizar</wizard-button>
              </div>
            </template>
          </form-wizard>
        </form>
      </validation-observer>
      <query-created
        @new-query="setConsultationTab"
        v-if="finished"
      />
      <loader v-if="loading"/>
    </div>
  </div>
</template>

<script>
import { FormWizard, TabContent, WizardButton } from 'vue-form-wizard';
import { ValidationObserver } from 'vee-validate';
import currency from '@/support/filters/currency';
import ErrorCantFetch from '@/compliance/components/new-query/messages/ErrorCantFetch.vue';
import Loader from '@/support/components/Loader.vue';
import StepConsultations from '@/compliance/components/new-query/StepConsultations.vue';
import StepInformation from '@/compliance/components/new-query/StepInformation.vue';
import StepFinish from '@/compliance/components/new-query/StepFinish.vue';
import ErrorCantCreate from '@/compliance/components/new-query/messages/ErrorCantCreate.vue';
import QueryCreated from '@/compliance/components/new-query/messages/QueryCreated.vue';
import { mapState } from 'vuex';
import { isEmpty } from 'lodash';

export default {
  name: 'NewQuery',

  props: {
    tag: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      finished: false,
      step: 0,
      errors: {
        cantFetch: false,
        unprocessableEntityWhenCreate: false,
        paymentRequiredWhenCreate: false,
        cantCreate: false,
      },
      form: {
        consultations: [],
        template: [],
      },
    };
  },

  methods: {
    /**
     * Load all available compliance.
     *
     * @returns {Promise<void>}
     */
    async loadRequiredData() {
      try {
        this.resetErrors();
        this.loading = true;

        await Promise.all([
          this.$store.dispatch('compliance/fetchAvailableConsultations', { tag: this.tag }),
          ['cpf', 'cnpj'].includes(this.tag) ? this.$store.dispatch('compliance/fetchAllTemplates', this.tag) : Promise.resolve(),
        ]);
      } catch {
        this.errors.cantFetch = true;
      } finally {
        this.loading = false;
      }
    },

    backToOthers() {
      this.$emit('update:tag', '');
    },

    /**
     * Reset the errors.
     */
    resetErrors() {
      this.errors.cantFetch = false;
      this.errors.unprocessableEntityWhenCreate = false;
      this.errors.paymentRequiredWhenCreate = false;
      this.errors.cantCreate = false;
    },

    /**
     * Validate the form.
     *
     * @returns {Promise<boolean>}
     */
    async validateForm() {
      this.resetErrors();
      await this.fetchTemplate();
      return !!(await this.$refs.form.validate());
    },

    async fetchTemplate() {
      if (this.form.template.id && this.step === 0) {
        this.loading = true;
        await this.$store.dispatch('template/fetchTemplateDetail', this.form.template.id);
        this.loading = false;
      }
    },

    /**
     * Create the consultation.
     *
     * @returns {Promise<void>}
     */
    async createQuery() {
      try {
        this.loading = true;
        this.resetErrors();

        await this.$store.dispatch('compliance/createQuery', { form: this.form, tag: this.tag });

        this.finished = true;
      } catch ({ response }) {
        this.errors.cantCreate = true;

        if (response.status === 422) {
          this.errors.unprocessableEntityWhenCreate = response.data;
        }

        if (response.status === 402) {
          this.errors.paymentRequiredWhenCreate = true;
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Reset the new compliance.
     */
    resetNewQuery() {
      this.form.consultations = [];
      this.form.template = [];

      Object.keys(this.form)
        .filter((field) => field !== 'consultations' && field !== 'template')
        .forEach((field) => {
          delete this.form[field];
        });

      this.resetErrors();
      this.step = 0;
      this.finished = false;
    },

    changeTab(prevIndex, nextIndex) {
      if (prevIndex !== nextIndex && nextIndex === 0) {
        this.resetNewQuery();
      }
    },

    setConsultationTab() {
      this.$refs.wizard.navigateToTab(0);
    },

    submitForm() {
    },
  },

  computed: {
    /**
     * The price from new query.
     *
     * @returns {*}
     */
    price() {
      if (this.form.consultations.length > 0) {
        return this.form
          .consultations
          .reduce((price) => price + parseFloat(this.user.plan.price_per_consultation), 0);
      }
      if (!isEmpty(this.form.template)) {
        return this.form.template.quantity * parseFloat(this.user.plan.price_per_consultation);
      }

      return 0;
    },

    ...mapState('auth', [
      'user',
    ]),
  },

  mounted() {
    this.loadRequiredData();
  },

  filters: {
    currency,
  },

  components: {
    FormWizard,
    TabContent,
    WizardButton,
    ValidationObserver,
    Loader,
    ErrorCantFetch,
    StepConsultations,
    StepInformation,
    StepFinish,
    ErrorCantCreate,
    QueryCreated,
  },
};
</script>

<style lang="scss" scoped>
  .wizard {
    .wizard-footer-header {
      @apply text-black;
      @apply text-xs;
      @apply italic;
      @apply mt-2;
    }

    .tab {
      &:not(.tab--only-show-error) {
        padding-bottom: 2em;
      }
    }
  }
</style>
